import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useStatsigUser, useStatsigClient } from '@statsig/react-bindings';
import { featureGates as statsigGates, experiments as statsigExperiments } from '../../utils/statsig';
import { getDefaultCustomFields } from '../../utils/statsigUserFields';

export const useStatsig = () => {
    const user = useSelector((state) => state.auth.user);
    const { user: statsigUser, updateUserAsync } = useStatsigUser();
    const { checkGate, getExperiment: getStatsigExperiment } = useStatsigClient();
    const featureGates = statsigGates;
    const experiments = statsigExperiments;

    const getExperiment = useCallback((experimentName) => {
        return getStatsigExperiment(experimentName);
    }, [getStatsigExperiment]);

    const [initializeUserData, setInitializeUserData] = useState({
        shouldInitialize: false,
        customFields: null,
    });

    const initializeStatsigUser = useCallback((customFields = null) => {
        setInitializeUserData({
            shouldInitialize: true,
            customFields,
        });
    }, []);

    const updateStatsigUser = async (customFields) => {
        const shouldUpdateUser = statsigUser?.userID && Object.keys(customFields).length > 0;
        const existingCustomFields = statsigUser?.custom || {};
        if (shouldUpdateUser) {
            const userData = { ...statsigUser, custom: { ...existingCustomFields, ...customFields } };
            await updateUserAsync(userData);
        }
    };

    const createStatsigUserData = (user) => {
        return {
            userID: user.id,
            email: user.email,
            custom: getDefaultCustomFields(user),
        };
    };

    useEffect(() => {
        const shouldInitialize = user && !statsigUser?.userID && initializeUserData.shouldInitialize;

        if (shouldInitialize) {
            const initializeUser = async () => {
                const statsigUserDataToUpdate = createStatsigUserData(user);
                if (initializeUserData.customFields && Object.keys(initializeUserData.customFields).length > 0) {
                    statsigUserDataToUpdate.custom = {
                        ...statsigUserDataToUpdate.custom,
                        ...initializeUserData.customFields,
                    };
                }
                await updateUserAsync(statsigUserDataToUpdate);
            };
            initializeUser();
            setInitializeUserData({
                shouldInitialize: false,
                customFields: null,
            });
        }
    }, [user, statsigUser, initializeUserData]);

    return {
        initializeStatsigUser,
        updateStatsigUser,
        checkGate,
        getExperiment,
        featureGates,
        experiments,
        statsigUser,
    };
};
